const FALLBACK_USD_TO_IDR = 15000;
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

let cachedRate = null;
let cachedAt = 0;
let pendingFetch = null;

function getEnvOverride() {
  const envValue = Number(process.env.REACT_APP_USD_TO_IDR_RATE);
  if (Number.isFinite(envValue) && envValue > 0) {
    return envValue;
  }
  return FALLBACK_USD_TO_IDR;
}

export function getCachedUsdToIdrRate() {
  if (cachedRate) {
    return cachedRate;
  }
  return getEnvOverride();
}

export async function ensureUsdToIdrRate() {
  const now = Date.now();
  if (cachedRate && now - cachedAt < CACHE_TTL_MS) {
    return cachedRate;
  }
  if (pendingFetch) {
    return pendingFetch;
  }

  pendingFetch = (async () => {
    try {
      const response = await fetch('/api/rates/usd-idr');
      if (!response.ok) {
        throw new Error(`Unexpected status ${response.status}`);
      }

      const data = await response.json();
      const rate = Number(data?.rate);
      const fallback = Number(data?.fallback);

      if (Number.isFinite(rate) && rate > 0) {
        cachedRate = rate;
        cachedAt = Date.now();
        return rate;
      }

      if (Number.isFinite(fallback) && fallback > 0) {
        cachedRate = fallback;
        cachedAt = Date.now();
        return fallback;
      }

      throw new Error('Invalid rate payload');
    } catch (error) {
      console.warn('⚠️ Failed to fetch USD→IDR rate, using fallback', error);
      cachedRate = getEnvOverride();
      cachedAt = Date.now();
      return cachedRate;
    } finally {
      pendingFetch = null;
    }
  })();

  return pendingFetch;
}

export function convertUsdToIdr(amountUsd, rate = getCachedUsdToIdrRate()) {
  const numericAmount = Number(amountUsd) || 0;
  return {
    rate,
    amountIdr: Math.round(numericAmount * rate),
  };
}

export function formatIdr(amountIdr) {
  const normalized = Math.round(Number(amountIdr) || 0);
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(normalized);
}

export function formatIdrFromUsd(amountUsd, rate = getCachedUsdToIdrRate()) {
  const { amountIdr } = convertUsdToIdr(amountUsd, rate);
  return formatIdr(amountIdr);
}

export function toDisplayAmount(amount, currency = 'USD') {
  // Xendit returns amounts in cents for USD, but in rupiah for IDR
  if (currency === 'IDR') {
    return Number(amount) || 0;
  }
  // For USD, convert cents to dollars
  return (Number(amount) || 0) / 100;
}
